import { useCallback, useEffect, useState } from 'react';
import type {
  IUnmatchedGitHubResponse,
  IUnmatchedGitHubRow,
  IUnmatchedSlackResponse,
  IUnmatchedSlackRow,
} from '@skystone/shared';
import { personApi } from '@/services/person-api';
import { getErrorMessage } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { ErrorBanner } from '@/components/ui';
import { UnmatchedSlackRow } from './UnmatchedSlackRow';
import { UnmatchedGitHubRow } from './UnmatchedGitHubRow';
import { LinkToPersonDialog } from './LinkToPersonDialog';
import { CreatePersonDialog } from './CreatePersonDialog';
import { MarkAsBotConfirmDialog } from './MarkAsBotConfirmDialog';
import {
  toReconcileTargetFromGitHub,
  toReconcileTargetFromSlack,
  type ReconcileTarget,
} from './reconcile-target';

interface UnmatchedTabProps {
  onActionCompleted?: () => void;
}

const EMPTY_SLACK: IUnmatchedSlackResponse = { missingEmail: [], emailPresent: [] };
const EMPTY_GITHUB: IUnmatchedGitHubResponse = { missingEmail: [], emailPresent: [] };

export function UnmatchedTab({ onActionCompleted }: Readonly<UnmatchedTabProps>) {
  const [slack, setSlack] = useState<IUnmatchedSlackResponse>(EMPTY_SLACK);
  const [github, setGithub] = useState<IUnmatchedGitHubResponse>(EMPTY_GITHUB);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [linkTarget, setLinkTarget] = useState<ReconcileTarget | null>(null);
  const [createTarget, setCreateTarget] = useState<ReconcileTarget | null>(null);
  const [markBotTarget, setMarkBotTarget] = useState<ReconcileTarget | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const [slackResponse, githubResponse] = await Promise.all([
        personApi.listUnmatchedSlack(),
        personApi.listUnmatchedGitHub(),
      ]);
      setSlack(slackResponse);
      setGithub(githubResponse);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to load unmatched accounts'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleActionCompleted = async (): Promise<void> => {
    setLinkTarget(null);
    setCreateTarget(null);
    setMarkBotTarget(null);
    await load();
    onActionCompleted?.();
  };

  const openSlackLink = (row: IUnmatchedSlackRow): void =>
    setLinkTarget(toReconcileTargetFromSlack(row));
  const openSlackCreate = (row: IUnmatchedSlackRow): void =>
    setCreateTarget(toReconcileTargetFromSlack(row));
  const openSlackMarkBot = (row: IUnmatchedSlackRow): void =>
    setMarkBotTarget(toReconcileTargetFromSlack(row));
  const openGitHubLink = (row: IUnmatchedGitHubRow): void =>
    setLinkTarget(toReconcileTargetFromGitHub(row));
  const openGitHubCreate = (row: IUnmatchedGitHubRow): void =>
    setCreateTarget(toReconcileTargetFromGitHub(row));
  const openGitHubMarkBot = (row: IUnmatchedGitHubRow): void =>
    setMarkBotTarget(toReconcileTargetFromGitHub(row));

  const slackMissingCount = slack.missingEmail.length;
  const slackGeneralCount = slack.emailPresent.length;
  const githubMissingCount = github.missingEmail.length;
  const githubGeneralCount = github.emailPresent.length;

  return (
    <div className="space-y-4">
      <ErrorBanner message={error} />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <span>Slack accounts missing email — manual review required</span>
            <span
              className="inline-flex min-w-[1.5rem] items-center justify-center rounded-full bg-warning-50 px-2 text-xs font-semibold text-warning-700"
              data-testid="slack-missing-email-count"
            >
              {slackMissingCount}
            </span>
          </CardTitle>
          <CardDescription>
            These Slack accounts have no email so auto-match cannot resolve them. Link or create a
            Person manually. This count is always shown so these accounts stay visible.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {slackMissingCount === 0 && !loading && (
            <p className="text-sm text-muted-foreground">
              No Slack accounts missing email.
            </p>
          )}
          {slack.missingEmail.map((row) => (
            <UnmatchedSlackRow
              key={row.slackId}
              row={row}
              onLink={openSlackLink}
              onCreate={openSlackCreate}
              onMarkAsBot={openSlackMarkBot}
            />
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <span>Unmatched Slack — no Person with matching email</span>
            <span
              className="inline-flex min-w-[1.5rem] items-center justify-center rounded-full bg-muted px-2 text-xs font-semibold"
              data-testid="slack-email-present-count"
            >
              {slackGeneralCount}
            </span>
          </CardTitle>
          <CardDescription>
            Slack accounts with an email that does not match any active Person (or matches more
            than one — <em>ambiguous</em>).
          </CardDescription>
        </CardHeader>
        <CardContent>
          {slackGeneralCount === 0 && !loading && (
            <p className="text-sm text-muted-foreground">
              All Slack accounts with emails are resolved.
            </p>
          )}
          {slack.emailPresent.map((row) => (
            <UnmatchedSlackRow
              key={row.slackId}
              row={row}
              onLink={openSlackLink}
              onCreate={openSlackCreate}
              onMarkAsBot={openSlackMarkBot}
            />
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <span>GitHub accounts missing email — manual review required</span>
            <span
              className="inline-flex min-w-[1.5rem] items-center justify-center rounded-full bg-warning-50 px-2 text-xs font-semibold text-warning-700"
              data-testid="github-missing-email-count"
            >
              {githubMissingCount}
            </span>
          </CardTitle>
          <CardDescription>
            GitHub accounts with no public email. Manual reconciliation required.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {githubMissingCount === 0 && !loading && (
            <p className="text-sm text-muted-foreground">
              No GitHub accounts missing email.
            </p>
          )}
          {github.missingEmail.map((row) => (
            <UnmatchedGitHubRow
              key={row.login}
              row={row}
              onLink={openGitHubLink}
              onCreate={openGitHubCreate}
              onMarkAsBot={openGitHubMarkBot}
            />
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <span>Unmatched GitHub — no Person with matching email</span>
            <span
              className="inline-flex min-w-[1.5rem] items-center justify-center rounded-full bg-muted px-2 text-xs font-semibold"
              data-testid="github-email-present-count"
            >
              {githubGeneralCount}
            </span>
          </CardTitle>
          <CardDescription>
            GitHub accounts with an email that does not match any active Person (or matches more
            than one — <em>ambiguous</em>).
          </CardDescription>
        </CardHeader>
        <CardContent>
          {githubGeneralCount === 0 && !loading && (
            <p className="text-sm text-muted-foreground">
              All GitHub accounts with emails are resolved.
            </p>
          )}
          {github.emailPresent.map((row) => (
            <UnmatchedGitHubRow
              key={row.login}
              row={row}
              onLink={openGitHubLink}
              onCreate={openGitHubCreate}
              onMarkAsBot={openGitHubMarkBot}
            />
          ))}
        </CardContent>
      </Card>

      <LinkToPersonDialog
        open={linkTarget !== null}
        target={linkTarget}
        onClose={() => setLinkTarget(null)}
        onConfirmed={handleActionCompleted}
      />
      <CreatePersonDialog
        open={createTarget !== null}
        target={createTarget}
        onClose={() => setCreateTarget(null)}
        onConfirmed={handleActionCompleted}
      />
      <MarkAsBotConfirmDialog
        open={markBotTarget !== null}
        target={markBotTarget}
        onClose={() => setMarkBotTarget(null)}
        onConfirmed={handleActionCompleted}
      />
    </div>
  );
}
